import type { ReactElement } from 'react';
import { Children, cloneElement } from 'react';
import { GestureResponderEvent, Linking } from 'react-native';
import { useLinkPressHandler } from 'react-router-native';

type HrefObj = { pathname: string; params?: Record<string, string> };

export type LinkProps = {
  replace?: boolean;
  href: string | HrefObj;
  children: ReactElement;
};

function normalizeToHrefObj(hrefProp: LinkProps['href']): HrefObj {
  if (typeof hrefProp !== 'string') {
    return hrefProp;
  }

  return { pathname: hrefProp };
}

/**
 * The purpose of this component is to provide an entrypoint to Links that we will be able to switch over to expo-router when we are ready.
 * Right now it maps react-router functionality to expo-router. The idea is for this component to be api-compatible with expo-router but
 * allow us to use it with the existing react-router setup.
 */

export function Link(props: LinkProps) {
  const { pathname: href, params: state } = normalizeToHrefObj(props.href);

  const handleLinkPress = useLinkPressHandler(href, { replace: props.replace, state });

  const child = Children.only(props.children);
  return cloneElement(child, {
    ...child.props,
    accessibilityRole: 'link',
    href: href,
    onPress: (evt: GestureResponderEvent) => {
      child.props?.onPress?.(evt);
      evt.preventDefault();
      if (href.startsWith('http')) {
        // External link
        Linking.openURL(href);
      } else {
        handleLinkPress(evt);
      }
    },
  });
}
